import { reactive, ref, computed, watch } from 'vue'
import { router } from '@/router'
import { KEYS } from '../data/mapPositions'
import { DESTINATIONS } from '../data/destinations'
import { drawLottery, getRanking, getLotteryRecord } from '../service'

// 全局共享状态（单例模式，所有组件共享同一实例）
const STORAGE_KEY = 'hongkong_imprint_collected'

function loadCollected() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

const collected = ref(loadCollected())

// 监听 collected 变化，自动保存到 localStorage
watch(collected, (val) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(val))
  } catch {
    // 静默失败
  }
}, { deep: true })

const currentChapter = ref('china')
const currentDest = ref(null)
const loginTokenRef = ref(sessionStorage.getItem('loginToken'))
const isLogged = computed(() => !!loginTokenRef.value)
const userNo = ref('000128')

// 当前页面（从路由派生）
const currentPage = computed(() => {
  const path = router.currentRoute.value.path
  if (path.startsWith('/hongKongImprint/')) {
    return path.replace('/hongKongImprint/', '')
  }
  return 'launch'
})

// 抽奖状态
const lotteryState = reactive({
  drawn: false,
  prizeName: null,
  prizeDetail: null,
  redemptionCode: null,
  claimType: null,
  addressSubmitted: false,
  isWon: false
})

// Toast 状态
const toastMessage = ref('')
const showToast = ref(false)

// 弹窗状态
const showLoginModal = ref(false)
const showPosterModal = ref(false)
const loginModalType = ref('product')

// Burst 特效状态
const showBurst = ref(false)
const burstKey = ref('')
const burstTextCn = ref('')
const isUnlocking = ref(false)
const pendingBadgeAnimate = ref(false)

// 排名编号
const rankingNo = ref(sessionStorage.getItem('hongkong_imprint_ranking') || '')

// 目的地固定遍历顺序
const CHINA_ORDER = ['anhui', 'jilin', 'xinjiang', 'hainan', 'shanxi', 'jiangsu']
const WORLD_ORDER = ['turkey', 'france', 'saudi', 'spain']

// 各章节的遍历起始索引（用户手动进入某个目的地时更新）
const chapterStartIndex = reactive({
  china: 0,
  world: 0
})

export function useAppState() {
  const totalKeys = KEYS.length
  const isCompleted = computed(() => collected.value.length >= totalKeys)

  const progressText = computed(() => {
    if (isCompleted.value) return '已集齐'
    return `${collected.value.length} / ${totalKeys}`
  })

  const codexStatusText = computed(() => {
    const count = collected.value.length
    if (count === 0) return 'START COLLECTING'
    if (!isCompleted.value) return `${count}/${totalKeys} · KEEP EXPLORING`
    return '✦ ALL COLLECTED ✦'
  })

  // 获取排名编号（登录 + 集齐 + sessionStorage 无缓存时调用接口）
  async function fetchRanking() {
    if (!isLogged.value || !isCompleted.value) return
    const cached = sessionStorage.getItem('hongkong_imprint_ranking')
    if (cached) {
      rankingNo.value = cached
      return
    }
    try {
      const res = await getRanking()
      console.log('排名接口返回:', res)
      const no = res?.rankingNo || ''
      if (no) {
        rankingNo.value = no
        sessionStorage.setItem('hongkong_imprint_ranking', no)
      }
    } catch (e) {
      console.error('获取排名失败:', e)
    }
  }

  // 获取抽奖记录（登录 + 集齐时调用）
  async function fetchLotteryRecord() {
    if (!isLogged.value || !isCompleted.value) return
    try {
      const res = await getLotteryRecord()
      console.log('抽奖记录接口返回:', res)
      // 有数据说明已抽奖
      if (res) {
        lotteryState.drawn = true
        lotteryState.prizeName = res?.prizeName || null
        lotteryState.prizeDetail = res?.prizeDetail || null
        lotteryState.redemptionCode = res?.redemptionCode || null
        lotteryState.isWon = res?.isWon ?? false
      }
    } catch (e) {
      console.error('获取抽奖记录失败:', e)
    }
  }

  function hasCollected(key) {
    return collected.value.includes(key)
  }

  function addToCollected(key) {
    if (!collected.value.includes(key)) {
      collected.value.push(key)
    }
  }

  function goPage(pageId) {
    router.push(`/hongKongImprint/${pageId}`)
  }

  function replacePage(pageId) {
    router.replace(`/hongKongImprint/${pageId}`)
  }

  function goResult() {
    const currentPath = router.currentRoute.value.path
    if (currentPath.includes('/badge')) {
      replacePage('result')
    } else {
      goPage('result')
    }
  }

  function goBadge() {
    const currentPath = router.currentRoute.value.path
    if (currentPath.includes('/result')) {
      replacePage('badge')
    } else {
      goPage('badge')
    }
  }

  function switchChapter(chapter) {
    currentChapter.value = chapter
  }

  function openDestination(key) {
    currentDest.value = key
    const chapter = DESTINATIONS[key]?.chapter
    const order = chapter === 'china' ? CHINA_ORDER : WORLD_ORDER
    const idx = order.indexOf(key)
    if (idx !== -1 && chapter) {
      chapterStartIndex[chapter] = idx
    }
    // 从 product 页面跳转时替换路由，避免历史栈堆积
    const currentPath = router.currentRoute.value.path
    if (currentPath.includes('/product')) {
      replacePage('destination')
    } else {
      goPage('destination')
    }
  }

  function openProduct(key) {
    currentDest.value = key
    const wasCollected = hasCollected(key)
    pendingBadgeAnimate.value = !wasCollected
    // 从 destination 页面跳转时替换路由，避免历史栈堆积
    const currentPath = router.currentRoute.value.path
    if (currentPath.includes('/destination')) {
      replacePage('product')
    } else {
      goPage('product')
    }
    // 首次进入产品页时立即触发点亮
    if (!wasCollected) {
      triggerStampUnlock(key)
    }
  }

  function triggerStampUnlock(key) {
    if (isUnlocking.value) return
    isUnlocking.value = true
    const d = DESTINATIONS[key]
    addToCollected(key)

    // 集齐第10个印记时记录完成时间并获取排名
    if (isCompleted.value) {
      const now = new Date()
      const dateStr = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')}`
      try {
        localStorage.setItem('hongkong_imprint_completed_date', dateStr)
      } catch {
        // 静默失败
      }
      fetchRanking()
    }

    // 预加载 burst 徽章图片
    const imgBase = import.meta?.env?.VITE_OBS_ASSETS_BASE_URL || 'https://ts-static.ourtour.com/obs/assets/'
    const imgUrl = `${imgBase}new-journey/hongkong-map-${key}1.png`
    const preloadImg = new Image()
    preloadImg.onload = () => startBurst(key, d)
    preloadImg.onerror = () => startBurst(key, d)
    preloadImg.src = imgUrl
  }

  function startBurst(key, d) {
    burstKey.value = key
    burstTextCn.value = `「${d.titleCn}」印记已收藏`
    showBurst.value = true

    setTimeout(() => {
      showBurst.value = false
      isUnlocking.value = false
      // 集齐全部印记后触发登录引导
      if (isCompleted.value && !isLogged.value) {
        setTimeout(() => {
          openLoginModal('product')
        }, 300)
      }
    }, 2000)
  }

  // 在指定顺序数组中，从 startIdx 开始环形查找下一个未收集的 key
  function findNextInOrder(order, startIdx) {
    const len = order.length
    for (let i = 0; i < len; i++) {
      const idx = (startIdx + i) % len
      const key = order[idx]
      if (!hasCollected(key)) {
        return key
      }
    }
    return null
  }

  function goNextDestination() {
    if (isCompleted.value) {
      if (!isLogged.value) {
        openLoginModal('product')
        return
      }
      goBadge()
      return
    }

    // 确定优先章节和次级章节
    const primaryChapter = currentChapter.value
    const secondaryChapter = primaryChapter === 'china' ? 'world' : 'china'
    const primaryOrder = primaryChapter === 'china' ? CHINA_ORDER : WORLD_ORDER
    const secondaryOrder = secondaryChapter === 'china' ? CHINA_ORDER : WORLD_ORDER

    // 先在优先章节中查找
    let nextKey = findNextInOrder(primaryOrder, chapterStartIndex[primaryChapter])

    // 优先章节找完，切换到次级章节
    if (!nextKey) {
      nextKey = findNextInOrder(secondaryOrder, chapterStartIndex[secondaryChapter])
    }

    // 次级章节也找完，再回头检查优先章节是否有遗漏
    if (!nextKey) {
      nextKey = findNextInOrder(primaryOrder, 0)
    }

    // 全部收集完毕
    if (!nextKey) {
      goBadge()
      return
    }

    const nextChapter = DESTINATIONS[nextKey].chapter
    if (nextChapter !== currentChapter.value) {
      currentChapter.value = nextChapter
    }
    openDestination(nextKey)
  }

  // Toast
  function toast(msg) {
    toastMessage.value = msg
    showToast.value = true
    setTimeout(() => {
      showToast.value = false
    }, 2000)
  }

  async function startDraw() {
    if (!isLogged.value) {
      openLoginModal('lottery')
      return
    }

    try {
      const res = await drawLottery()
      lotteryState.drawn = true
      lotteryState.prizeName = res?.prizeName || null
      lotteryState.prizeDetail = res?.prizeDetail || null
      lotteryState.redemptionCode = res?.redemptionCode || null
      lotteryState.isWon = res?.isWon ?? false
      goResult()
    } catch (error) {
      console.error('抽奖失败:', error)
      toast('抽奖失败，请重试')
    }
  }

  function openLottery() {
    if (!isCompleted.value) {
      toast('集齐十枚印记方可参与抽奖')
      return
    }
    if (!isLogged.value) {
      openLoginModal('launch')
      return
    }
    goPage('lottery')
  }

  function openLoginModal(type = 'product') {
    loginModalType.value = type
    showLoginModal.value = true
  }

  function openCoupon() {
    if (!isLogged.value) {
      openLoginModal('coupon')
      return
    }
    goPage('coupon')
  }


  function refreshLoginState() {
    loginTokenRef.value = sessionStorage.getItem('loginToken')
  }

  return {
    // 状态
    currentPage,
    collected,
    currentChapter,
    currentDest,
    isLogged,
    userNo,
    lotteryState,
    toastMessage,
    showToast,
    showLoginModal,
    showPosterModal,
    loginModalType,
    showBurst,
    burstKey,
    burstTextCn,
    isUnlocking,
    pendingBadgeAnimate,
    totalKeys,
    isCompleted,
    progressText,
    codexStatusText,
    rankingNo,

    // 方法
    hasCollected,
    fetchRanking,
    fetchLotteryRecord,
    addToCollected,
    goPage,
    switchChapter,
    openDestination,
    openProduct,
    triggerStampUnlock,
    goNextDestination,
    toast,
    startDraw,
    openLottery,
    openLoginModal,
    openCoupon,
    refreshLoginState,
    goResult,
    goBadge
  }
}

// 模块级别导出：供 main.js 在异步初始化完成后同步 loginToken
export function syncLoginToken() {
  loginTokenRef.value = sessionStorage.getItem('loginToken')
}
